import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { ChevronsUpDown, Check, Search } from "lucide-react";
import { ProductoCombobox } from "@/components/ProductoCombobox";
import { toast } from "sonner";
import { TrendingDown, TrendingUp, Minus, Trophy, BarChart3, GitCompare } from "lucide-react";

// Modos del comparador
const MODE_BASE = "base";          // Producto base (sin marca) — agrupa todos los laboratorios
const MODE_COMERCIAL = "comercial"; // Producto + marca específica

export default function CostComparator() {
  const [productos, setProductos] = useState([]);
  const [productosBase, setProductosBase] = useState([]);
  const [topConflictos, setTopConflictos] = useState([]);
  const [topConflictosBase, setTopConflictosBase] = useState([]);

  const [mode, setMode] = useState(MODE_BASE);
  const [productoId, setProductoId] = useState("");        // pc_id (modo comercial)
  const [productoBaseId, setProductoBaseId] = useState(""); // pb_id (modo base)

  const [comparativa, setComparativa] = useState(null);
  const [historico, setHistorico] = useState(null);
  const [historicoPcId, setHistoricoPcId] = useState(""); // PC seleccionado para histórico (cuando hay varias marcas)
  const [proveedorFilter, setProveedorFilter] = useState("__all__");
  const [tab, setTab] = useState("proveedores");

  // Carga inicial
  useEffect(() => {
    Promise.all([
      api.get("/masters/productos-comerciales"),
      api.get("/masters/productos-base"),
      api.get("/purchases/comparador-costos/productos-con-multiples-proveedores").catch(() => ({ data: [] })),
      api.get("/purchases/comparador-costos/productos-base-con-multiples-proveedores").catch(() => ({ data: [] })),
    ]).then(([p, pb, t, tb]) => {
      setProductos(p.data || []);
      setProductosBase(pb.data || []);
      setTopConflictos(t.data || []);
      setTopConflictosBase(tb.data || []);
    }).catch(e => toast.error(formatApiError(e)));
  }, []);

  // Carga datos cuando cambia el producto seleccionado
  useEffect(() => {
    (async () => {
      if (mode === MODE_COMERCIAL && productoId) {
        try {
          const [c, h] = await Promise.all([
            api.get(`/purchases/comparador-costos/proveedores/${productoId}`),
            api.get(`/purchases/comparador-costos/historico/${productoId}`),
          ]);
          setComparativa(c.data);
          setHistorico(h.data);
          setHistoricoPcId(productoId);
          setProveedorFilter("__all__");
        } catch (e) { toast.error(formatApiError(e)); }
      } else if (mode === MODE_BASE && productoBaseId) {
        try {
          const c = await api.get(`/purchases/comparador-costos/proveedores-base/${productoBaseId}`);
          setComparativa(c.data);
          setProveedorFilter("__all__");
          // Para histórico se requiere elegir una marca puntual
          const firstPc = (c.data?.comerciales || [])[0]?.id || "";
          setHistoricoPcId(firstPc);
          if (firstPc) {
            const h = await api.get(`/purchases/comparador-costos/historico/${firstPc}`);
            setHistorico(h.data);
          } else {
            setHistorico(null);
          }
        } catch (e) { toast.error(formatApiError(e)); }
      } else {
        setComparativa(null);
        setHistorico(null);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, productoId, productoBaseId]);

  // Cuando el usuario elige otra marca en histórico (modo base)
  const cambiarHistoricoPc = async (pcId) => {
    setHistoricoPcId(pcId);
    if (!pcId) { setHistorico(null); return; }
    try {
      const h = await api.get(`/purchases/comparador-costos/historico/${pcId}`);
      setHistorico(h.data);
      setProveedorFilter("__all__");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const cambiarModo = (newMode) => {
    setMode(newMode);
    setProductoId("");
    setProductoBaseId("");
    setComparativa(null);
    setHistorico(null);
    setHistoricoPcId("");
  };

  const productoSeleccionado = mode === MODE_BASE ? productoBaseId : productoId;

  const historicoFiltrado = useMemo(() => {
    if (!historico?.historico) return [];
    if (proveedorFilter === "__all__") return historico.historico;
    return historico.historico.filter(h => h.proveedor_id === proveedorFilter);
  }, [historico, proveedorFilter]);

  const variacionStats = useMemo(() => {
    if (!historicoFiltrado.length) return null;
    const costos = historicoFiltrado.map(h => h.costo_unitario || 0).filter(c => c > 0);
    if (!costos.length) return null;
    const min = Math.min(...costos);
    const max = Math.max(...costos);
    const first = costos[0];
    const last = costos[costos.length - 1];
    const cambio = first > 0 ? ((last - first) / first) * 100 : 0;
    return { min, max, first, last, cambio: cambio.toFixed(2), n: costos.length };
  }, [historicoFiltrado]);

  // Variantes con misma base pero distinta marca (para selector en histórico modo base)
  const comercialesDelBase = comparativa?.comerciales || [];

  return (
    <div className="space-y-6" data-testid="cost-comparator-page">
      <header>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5c7368] mb-1">Supervisión</div>
        <h1 className="text-3xl font-bold tracking-tight text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>
          <GitCompare className="inline mr-2 mb-1" size={28} /> Comparador de costos
        </h1>
        <p className="text-sm text-[#4a5c53] mt-1">
          Compara últimos costos por proveedor para un mismo producto y revisa la variación de costos a lo largo del tiempo.
        </p>
      </header>

      {/* Selector de modo + producto */}
      <div className="bg-white rounded-md border border-[#d1dbd6] p-4 space-y-3">
        <div className="flex gap-2 flex-wrap">
          <span className="text-xs uppercase tracking-wider font-semibold text-[#5c7368] self-center mr-1">Comparar por:</span>
          <button
            type="button"
            onClick={() => cambiarModo(MODE_BASE)}
            className={`text-xs px-3 py-1.5 rounded-md border-2 font-semibold transition-colors ${mode === MODE_BASE ? "bg-[#205c45] text-white border-[#205c45]" : "bg-white text-[#5c7368] border-[#d1dbd6] hover:border-[#205c45]"}`}
            data-testid="mode-base-btn"
          >
            Producto base (sin marca)
          </button>
          <button
            type="button"
            onClick={() => cambiarModo(MODE_COMERCIAL)}
            className={`text-xs px-3 py-1.5 rounded-md border-2 font-semibold transition-colors ${mode === MODE_COMERCIAL ? "bg-[#205c45] text-white border-[#205c45]" : "bg-white text-[#5c7368] border-[#d1dbd6] hover:border-[#205c45]"}`}
            data-testid="mode-comercial-btn"
          >
            Producto + marca
          </button>
          <span className="text-[11px] text-[#5c7368] self-center ml-2">
            {mode === MODE_BASE
              ? "Agrupa todos los laboratorios/marcas del mismo principio activo y concentración."
              : "Filtra por marca específica del producto."}
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div className="md:col-span-2">
            <div className="text-xs uppercase tracking-wider font-semibold text-[#5c7368] mb-1.5">
              {mode === MODE_BASE ? "Producto base a comparar" : "Producto comercial a comparar"}
            </div>
            {mode === MODE_BASE ? (
              <ProductoBaseCombobox
                value={productoBaseId}
                onChange={setProductoBaseId}
                productos={productosBase}
                testId="comparator-base"
              />
            ) : (
              <ProductoCombobox
                value={productoId}
                onChange={setProductoId}
                productos={productos}
                testId="comparator-prod"
                placeholder="Buscar producto comercial…"
              />
            )}
          </div>
          <div className="text-xs text-[#5c7368]">
            {mode === MODE_BASE && topConflictosBase.length > 0 && (
              <div>
                <div className="font-semibold mb-1">Top productos base con varios proveedores:</div>
                <div className="flex flex-wrap gap-1">
                  {topConflictosBase.slice(0, 8).map(p => (
                    <button
                      key={p.id}
                      onClick={() => setProductoBaseId(p.id)}
                      className="text-[10px] px-2 py-0.5 rounded border bg-[#e9f2ee] text-[#205c45] border-[#b2d0c2] hover:bg-[#205c45] hover:text-white transition-colors"
                      data-testid={`top-base-${p.id}`}
                    >
                      {p.principio_activo} {p.concentracion ? `· ${p.concentracion}` : ""} <span className="opacity-60">· {p.n_proveedores}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {mode === MODE_COMERCIAL && topConflictos.length > 0 && (
              <div>
                <div className="font-semibold mb-1">Top productos comprados a varios proveedores:</div>
                <div className="flex flex-wrap gap-1">
                  {topConflictos.slice(0, 8).map(p => (
                    <button
                      key={p.id}
                      onClick={() => setProductoId(p.id)}
                      className="text-[10px] px-2 py-0.5 rounded border bg-[#e9f2ee] text-[#205c45] border-[#b2d0c2] hover:bg-[#205c45] hover:text-white transition-colors"
                      data-testid={`top-prod-${p.id}`}
                    >
                      {p.nombre_comercial} <span className="opacity-60">· {p.n_proveedores}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Resumen de marcas agrupadas en modo base */}
        {mode === MODE_BASE && comparativa && comercialesDelBase.length > 0 && (
          <div className="text-[11px] text-[#205c45] flex items-center gap-2 flex-wrap pt-1" data-testid="marcas-incluidas">
            <span className="font-semibold uppercase tracking-wider">Marcas incluidas en esta comparación:</span>
            {comercialesDelBase.map(c => (
              <Badge key={c.id} variant="outline" className="bg-[#e9f2ee] border-[#b2d0c2] text-[#205c45]">
                {c.nombre_comercial}{c.laboratorio_nombre ? ` · ${c.laboratorio_nombre}` : ""}
              </Badge>
            ))}
          </div>
        )}
      </div>

      {productoSeleccionado && comparativa && (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="proveedores" data-testid="tab-proveedores"><BarChart3 size={14} className="mr-1.5" />Por proveedor (lado a lado)</TabsTrigger>
            <TabsTrigger value="historico" data-testid="tab-historico"><TrendingUp size={14} className="mr-1.5" />Histórico de variación</TabsTrigger>
          </TabsList>

          <TabsContent value="proveedores" className="space-y-4">
            {(comparativa.proveedores || []).length === 0 ? (
              <div className="rounded-md border border-dashed border-[#d1dbd6] bg-white p-10 text-center text-[#8ba397]">
                Este producto aún no tiene compras registradas.
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="bg-white border border-[#d1dbd6] rounded-md p-4">
                    <div className="text-[10px] uppercase tracking-wider text-[#5c7368]">Proveedores</div>
                    <div className="text-2xl font-bold text-[#15241d]">{comparativa.proveedores.length}</div>
                  </div>
                  <div className="bg-white border border-[#d1dbd6] rounded-md p-4">
                    <div className="text-[10px] uppercase tracking-wider text-[#5c7368]">Mejor costo (último)</div>
                    <div className="text-2xl font-bold text-[#205c45]">${comparativa.proveedores[0]?.ultimo_costo?.toFixed(4) ?? "—"}</div>
                  </div>
                  <div className="bg-white border border-[#d1dbd6] rounded-md p-4">
                    <div className="text-[10px] uppercase tracking-wider text-[#5c7368]">Spread (max - min)</div>
                    <div className="text-2xl font-bold text-[#8a5500]">${comparativa.spread?.toFixed(4) || "0.0000"}</div>
                  </div>
                </div>

                <div className="bg-white rounded-md border border-[#d1dbd6] overflow-x-auto">
                  <table className="erp-table">
                    <thead>
                      <tr>
                        <th className="w-10"></th>
                        <th>Proveedor</th>
                        {mode === MODE_BASE && <th>Última marca comprada</th>}
                        <th className="text-right">Último costo</th>
                        <th className="text-right">Promedio</th>
                        <th className="text-right">Min · Max</th>
                        <th className="text-right">Compras</th>
                        <th className="text-right">Cant. total</th>
                        <th>Última fecha</th>
                        <th className="text-right">Δ vs caro</th>
                      </tr>
                    </thead>
                    <tbody data-testid="proveedores-table">
                      {comparativa.proveedores.map((p) => (
                        <tr key={p.proveedor_id} className={p.es_mejor ? "bg-[#e9f2ee]" : ""} data-testid={`row-prov-${p.proveedor_id}`}>
                          <td className="text-center">
                            {p.es_mejor && <Trophy size={14} className="text-[#205c45] inline" />}
                          </td>
                          <td className="font-medium">{p.proveedor_nombre}</td>
                          {mode === MODE_BASE && (
                            <td className="text-xs text-[#4a5c53]">
                              {p.ultimo_producto_comprado || "—"}
                              {p.ultimo_laboratorio && <div className="text-[10px] text-[#8ba397]">{p.ultimo_laboratorio}</div>}
                            </td>
                          )}
                          <td className={`text-right tabular-nums font-semibold ${p.es_mejor ? "text-[#205c45]" : "text-[#15241d]"}`}>
                            ${p.ultimo_costo.toFixed(4)}
                          </td>
                          <td className="text-right tabular-nums text-[#5c7368]">${p.costo_promedio.toFixed(4)}</td>
                          <td className="text-right tabular-nums text-[11px] text-[#5c7368]">
                            ${p.costo_minimo.toFixed(4)} · ${p.costo_maximo.toFixed(4)}
                          </td>
                          <td className="text-right tabular-nums">{p.compras_total}</td>
                          <td className="text-right tabular-nums">{p.cantidad_total}</td>
                          <td className="text-xs text-[#5c7368]">{p.ultima_fecha || "—"}</td>
                          <td className="text-right tabular-nums">
                            {p.diferencia_vs_max > 0 ? (
                              <span className="text-[#205c45] font-semibold">-${p.diferencia_vs_max.toFixed(4)}</span>
                            ) : (
                              <span className="text-[#8ba397]">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-[11px] text-[#5c7368] italic">
                  {mode === MODE_BASE
                    ? "Los costos se agruparon ignorando marca/laboratorio. La columna 'Última marca comprada' indica qué presentación cubre cada proveedor."
                    : "Comparando solo la marca seleccionada. Considera además lead time y calidad antes de migrar volumen."}
                </p>
              </>
            )}
          </TabsContent>

          <TabsContent value="historico" className="space-y-4">
            {/* En modo base permitimos elegir la marca específica para ver su histórico */}
            {mode === MODE_BASE && comercialesDelBase.length > 1 && (
              <div className="bg-[#fef7ec] border border-[#f5c477] rounded-md p-3 flex flex-wrap items-center gap-3">
                <span className="text-xs uppercase tracking-wider font-semibold text-[#8a5500]">Marca para el histórico:</span>
                <Select value={historicoPcId} onValueChange={cambiarHistoricoPc}>
                  <SelectTrigger className="w-80 h-9" data-testid="hist-marca-selector"><SelectValue placeholder="Seleccionar marca/laboratorio…" /></SelectTrigger>
                  <SelectContent>
                    {comercialesDelBase.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.nombre_comercial}{c.laboratorio_nombre ? ` · ${c.laboratorio_nombre}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-[11px] text-[#8a5500]">
                  El histórico es por marca individual (no se mezclan las marcas para no distorsionar la variación).
                </span>
              </div>
            )}

            <div className="bg-white border border-[#d1dbd6] rounded-md p-3 flex flex-wrap items-center gap-3">
              <span className="text-xs uppercase tracking-wider font-semibold text-[#5c7368]">Filtrar por proveedor:</span>
              <Select value={proveedorFilter} onValueChange={setProveedorFilter}>
                <SelectTrigger className="w-72" data-testid="hist-prov-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todos los proveedores</SelectItem>
                  {(comparativa.proveedores || []).map(p => (
                    <SelectItem key={p.proveedor_id} value={p.proveedor_id}>{p.proveedor_nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {variacionStats && (
                <div className="ml-auto flex gap-3 text-xs">
                  <Badge variant="outline">N={variacionStats.n}</Badge>
                  <Badge variant="outline">Min: ${variacionStats.min.toFixed(4)}</Badge>
                  <Badge variant="outline">Max: ${variacionStats.max.toFixed(4)}</Badge>
                  <Badge className={Number(variacionStats.cambio) > 0 ? "bg-[#fef7ec] text-[#8a5500] border border-[#f5c477]" : Number(variacionStats.cambio) < 0 ? "bg-[#e9f2ee] text-[#205c45] border border-[#b2d0c2]" : "bg-white text-[#5c7368] border border-[#d1dbd6]"}>
                    {Number(variacionStats.cambio) > 0 ? <TrendingUp size={11} className="inline mr-1" /> : Number(variacionStats.cambio) < 0 ? <TrendingDown size={11} className="inline mr-1" /> : <Minus size={11} className="inline mr-1" />}
                    Variación total: {variacionStats.cambio}%
                  </Badge>
                </div>
              )}
            </div>

            {historicoFiltrado.length === 0 ? (
              <div className="rounded-md border border-dashed border-[#d1dbd6] bg-white p-10 text-center text-[#8ba397]">
                {mode === MODE_BASE && !historicoPcId
                  ? "Seleccione una marca para ver su histórico de variación."
                  : "No hay histórico para los filtros seleccionados."}
              </div>
            ) : (
              <div className="bg-white rounded-md border border-[#d1dbd6] overflow-x-auto">
                <table className="erp-table">
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Proveedor</th>
                      <th>N° factura</th>
                      <th className="text-right">Cantidad</th>
                      <th className="text-right">Costo unit.</th>
                      <th className="text-right">Variación</th>
                    </tr>
                  </thead>
                  <tbody data-testid="historico-table">
                    {historicoFiltrado.map((h, i) => (
                      <tr key={`${h.numero_factura}-${i}`}>
                        <td className="text-xs">{h.fecha_factura}</td>
                        <td className="font-medium">{h.proveedor_nombre}</td>
                        <td className="font-mono text-xs">{h.numero_factura}</td>
                        <td className="text-right tabular-nums">{h.cantidad}</td>
                        <td className="text-right tabular-nums font-semibold">${h.costo_unitario.toFixed(4)}</td>
                        <td className="text-right tabular-nums">
                          {i === 0 ? (
                            <span className="text-[#8ba397]">— inicial —</span>
                          ) : h.variacion_pct > 0 ? (
                            <span className="text-[#b53a25] font-semibold"><TrendingUp size={11} className="inline" /> +{h.variacion_pct}%</span>
                          ) : h.variacion_pct < 0 ? (
                            <span className="text-[#205c45] font-semibold"><TrendingDown size={11} className="inline" /> {h.variacion_pct}%</span>
                          ) : (
                            <span className="text-[#5c7368]">0%</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>
        </Tabs>
      )}

      {!productoSeleccionado && (
        <div className="rounded-md border border-dashed border-[#d1dbd6] bg-white p-12 text-center" data-testid="cost-comparator-empty">
          <GitCompare size={32} className="mx-auto mb-3 text-[#8ba397]" />
          <p className="text-[#4a5c53]">
            {mode === MODE_BASE
              ? "Selecciona un producto base (principio activo + concentración) para comparar todas sus marcas a la vez."
              : "Selecciona un producto comercial específico para comparar solo esa marca."}
          </p>
        </div>
      )}
    </div>
  );
}

/** Combobox simple para Producto Base */
function ProductoBaseCombobox({ value, onChange, productos = [], testId = "producto-base-combo" }) {
  const [open, setOpen] = useState(false);
  const selected = productos.find(p => p.id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open}
          className={`h-9 justify-between font-normal text-left w-full ${!selected ? "text-[#8ba397]" : ""}`}
          data-testid={`${testId}-trigger`}>
          <span className="truncate">
            {selected ? `${selected.principio_activo}${selected.concentracion ? " · " + selected.concentracion : ""}` : "Buscar producto base (principio activo)…"}
          </span>
          <ChevronsUpDown size={14} className="ml-2 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[min(420px,calc(100vw-24px))] max-w-[420px] p-0" align="start">
        <Command shouldFilter={true} filter={(v, s) => v.toLowerCase().includes(s.toLowerCase()) ? 1 : 0}>
          <div className="flex items-center border-b border-[#d1dbd6] px-3">
            <Search size={14} className="mr-2 shrink-0 opacity-50" />
            <CommandInput placeholder="Escriba para filtrar…" className="flex h-10 w-full bg-transparent py-3 text-sm outline-none placeholder:text-[#8ba397]" data-testid={`${testId}-input`} />
          </div>
          <CommandList className="max-h-[320px] overflow-y-auto">
            <CommandEmpty className="py-4 text-center text-sm text-[#8ba397]">Sin resultados</CommandEmpty>
            <CommandGroup>
              {productos.map(p => {
                const searchable = [p.principio_activo, p.concentracion, p.forma_farmaceutica].filter(Boolean).join(" ");
                return (
                  <CommandItem
                    key={p.id}
                    value={searchable}
                    onSelect={() => { onChange(p.id); setOpen(false); }}
                    data-testid={`${testId}-item-${p.id}`}
                  >
                    <Check size={14} className={`mr-2 ${value === p.id ? "opacity-100 text-[#205c45]" : "opacity-0"}`} />
                    <div className="flex flex-col">
                      <span className="font-medium">{p.principio_activo}</span>
                      <span className="text-[11px] text-[#5c7368]">
                        {[p.concentracion, p.forma_farmaceutica].filter(Boolean).join(" · ")}
                      </span>
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
